import React from 'react';
import { Button, Card, Row, Col, Typography, Space, Divider, Alert } from 'antd';
import { 
  ShopOutlined, 
  GiftOutlined, 
  DollarOutlined, 
  RocketOutlined,
  TeamOutlined,
  SafetyCertificateOutlined,
  BarChartOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import './Home.css';

const { Title, Paragraph, Text } = Typography;

const Home: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated, stationOwner } = useAuth();

  const features = [
    {
      icon: <ShopOutlined style={{ fontSize: '32px', color: '#DC143C' }} />,
      title: 'İstasyon Yönetimi',
      description: 'İstasyonlarınızı kolayca ekleyin, düzenleyin ve yönetin. Detaylı bilgiler ve konum yönetimi ile müşterilerinize en iyi hizmeti sunun.'
    },
    {
      icon: <GiftOutlined style={{ fontSize: '32px', color: '#FF6B35' }} />,
      title: 'Kampanya Oluşturma',
      description: 'Özel kampanyalar oluşturun, müşterilerinize özel indirimler sunun. Kampanyalarınızı kolayca yönetin ve takip edin.'
    },
    {
      icon: <DollarOutlined style={{ fontSize: '32px', color: '#52c41a' }} />,
      title: 'Gelir Takibi',
      description: 'Gelirlerinizi detaylı olarak takip edin. Aylık ve yıllık raporlar ile işletmenizin performansını analiz edin.'
    },
    {
      icon: <BarChartOutlined style={{ fontSize: '32px', color: '#1890ff' }} />,
      title: 'İstatistikler',
      description: 'Detaylı istatistikler ve raporlarla işletmenizin durumunu anlık olarak takip edin. Veri odaklı kararlar alın.'
    },
    {
      icon: <SafetyCertificateOutlined style={{ fontSize: '32px', color: '#faad14' }} />,
      title: 'Güvenli Platform',
      description: 'Verileriniz SSL şifreleme ile korunur. Güvenli ödeme sistemleri ve veri yedekleme ile işletmeniz güvende.'
    },
    {
      icon: <TeamOutlined style={{ fontSize: '32px', color: '#722ed1' }} />,
      title: '7/24 Destek',
      description: 'Uzman destek ekibimiz her zaman yanınızda. Sorularınız için 7/24 canlı destek hizmetimizden yararlanın.'
    }
  ];

  return (
    <div className="home-container">
      {/* Hero Section */}
      <div className="hero-section">
        <div className="hero-content">
          <div className="hero-badge">
            <RocketOutlined /> <span>YakıtGo İstasyon Sahipleri Platformu</span>
          </div>
          <Title level={1} className="hero-title">
            İstasyonunuzu Dijitalleştirin,
            <br />
            <span className="gradient-text">Müşterilerinize Ulaşın</span>
          </Title>
          <Paragraph className="hero-description">
            YakıtGo ile istasyonunuzu online platforma taşıyın, kampanyalar oluşturun 
            ve müşterilerinize özel teklifler sunun. Binlerce kullanıcıya ulaşın ve 
            işletmenizi büyütün.
          </Paragraph>
          
          {!isAuthenticated ? (
            <Space size="large" className="hero-buttons">
              <Button 
                type="primary" 
                size="large"
                icon={<ShopOutlined />}
                onClick={() => navigate('/register')}
                className="cta-button"
              >
                Hemen Üye Ol
              </Button>
              <Button 
                size="large"
                onClick={() => navigate('/login')}
                className="secondary-button"
              >
                Giriş Yap
              </Button>
            </Space>
          ) : !stationOwner?.isActive ? (
            <div>
              <Alert
                message="Üyeliğiniz Onay İçin Beklemektedir"
                description="Hesabınız admin tarafından onaylandıktan sonra panele erişebileceksiniz. Onay süreci genellikle 24 saat içinde tamamlanır."
                type="warning"
                showIcon
                style={{ maxWidth: '600px', margin: '0 auto 20px' }}
              />
              <Button 
                size="large"
                onClick={() => navigate('/login')}
                className="secondary-button"
              >
                Tekrar Giriş Yap
              </Button>
            </div>
          ) : (
            <Button 
              type="primary" 
              size="large"
              icon={<ShopOutlined />}
              onClick={() => navigate('/panel')}
              className="cta-button"
            >
              Panele Git
            </Button>
          )}
        </div>
      </div>

      {/* Features Section */}
      <div className="features-section">
        <div className="section-container">
          <Title level={2} className="section-title">
            Neden YakıtGo?
          </Title>
          <Paragraph className="section-description">
            İstasyonunuzu dijital platforma taşıyın ve binlerce müşteriye ulaşın
          </Paragraph>
          
          <Row gutter={[24, 24]} className="features-grid">
            {features.map((feature, index) => (
              <Col xs={24} sm={12} lg={8} key={index}>
                <Card 
                  className="feature-card"
                  hoverable
                >
                  <div className="feature-icon">{feature.icon}</div>
                  <Title level={4} className="feature-title">
                    {feature.title}
                  </Title>
                  <Paragraph className="feature-description">
                    {feature.description}
                  </Paragraph>
                </Card>
              </Col>
            ))}
          </Row>
        </div>
      </div>

      {/* CTA Section */}
      <div className="cta-section">
        <div className="section-container">
          <Card className="cta-card">
            <Title level={2} className="cta-title">
              Hemen Başlayın
            </Title>
            <Paragraph className="cta-description">
              Ücretsiz kayıt olun, istasyonunuzu ekleyin ve müşterilerinize ulaşmaya başlayın.
              <br />
              İlk ay ücretsiz deneme fırsatı!
            </Paragraph>
            {!isAuthenticated && (
              <Button 
                type="primary" 
                size="large"
                icon={<ShopOutlined />}
                onClick={() => navigate('/register')}
                className="cta-button-large"
              >
                Ücretsiz Kayıt Ol
              </Button>
            )}
          </Card>
        </div>
      </div>

      {/* Footer */}
      <div className="home-footer">
        <div className="section-container">
          <Row gutter={[32, 32]}>
            <Col xs={24} md={8}>
              <Title level={4}>YakıtGo</Title>
              <Paragraph>
                Türkiye'nin en büyük yakıt istasyonu platformu. 
                İstasyon sahipleri ve müşterileri bir araya getiriyoruz.
              </Paragraph>
            </Col>
            <Col xs={24} md={8}>
              <Title level={5}>Hızlı Linkler</Title>
              <Space direction="vertical">
                <Button type="link" onClick={() => navigate('/register')}>
                  Üye Ol
                </Button>
                <Button type="link" onClick={() => navigate('/login')}>
                  Giriş Yap
                </Button>
              </Space>
            </Col>
          </Row>
          <Divider />
          <div style={{ textAlign: 'center', color: '#999' }}>
            <Text>© 2024 <a href="https://www.hwyazilim.com/" target="_blank" rel="noopener noreferrer" style={{ color: '#999', textDecoration: 'none' }}>YakıtGo</a>. Tüm hakları saklıdır.</Text>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Home;

